// /src/app/modules/searchModule.js

import { take, call, put, all } from 'redux-saga/effects'
import * as api from '../services/api'
import { KIND, SCOPE, STATUS } from '../modules/constants'
import { cloneDeep } from '../modules/utilities'

const defaultResponse =
{
  companyNumber: "",
  statusCode: 200,
  reasonPhrase: "",
  items: [],
  kind: KIND.SEARCH.COMPANIES
}

const InitSearchResults = {
  ...defaultResponse,
  searchString: "",
}

const initialState = {
  submitting: false,
  newSearch: false,
  previousSearchString: '',
  searchResults: InitSearchResults,
  showErrorModal: false,
  statusCode: 200,
  tabIndex: 0,
  synced: false,
  scope: SCOPE.COMPANIES,
}

export function reducer(state = initialState, action) {
  const { httpResponseMessage, newSearch, scope, searchResults, searchString, seed, submitting, synced } = action
  switch (action.type) {

    case 'search.submit': {
      return {
        ...state,
        submitting: scope,
      }
    }

    case 'search.random': {
      return {
        ...state,
        submitting: SCOPE.RANDOM,
      }
    }

    case 'search.saveResults': {
      // console.log(`search.saveResults: `, action)
      var showErrorModal = !httpResponseMessage.isSuccessStatusCode
      return {
        ...state,
        ...httpResponseMessage,
        searchResults,
        previousSearchString: searchString,
        scope,
        seed,
        newSearch: showErrorModal || newSearch,
        submitting,
        showErrorModal,
        synced: false,
      }
    }

    case 'search.searchStringChanged': {
      // console.log('search.searchStringChanged action:', action)
      return {
        ...state,
        previousSearchString: searchString,
        newSearch: true,
        submitting: false,
      }
    }

    case 'search.updateSearchResults': {
      // console.log('search.updateSearchResults action:', action)
      return {
        ...state,
        searchResults,
        scope: scope || state.scope,
        synced: true,
      }
    }

    case 'search.setSynced': {
      // console.log('search.setSynced action:', action)
      return {
        ...state,
        synced,
      }
    }

    case 'search.setShowErrorModal': {
      // console.log(`search.setShowErrorModal action: `, action)
      return {
        ...state,
        customReason: action.customReason,
        reasonPhrase: action.reasonPhrase,
        showErrorModal: !action.isSuccessStatusCode,
        statusCode: action.statusCode,
        status: STATUS.NEXUS.CLEAR,
      }
    }

    default: {
      return state
    }
  }
}

function* watchSubmit() {
  while (true) {
    const props = yield take('search.submit')
    // console.log('search.submit props:', props)
    const { scope, searchString, stateSearchResults } = props

    var searchResults = {}

    try {
      if (stateSearchResults[scope].searchString === searchString)
        searchResults = stateSearchResults[scope]
      else
        searchResults = yield call(api.companiesHouse.search, { scope, searchString })

      const httpResponseMessage = searchResults.httpResponseMessage

      if (httpResponseMessage.isSuccessStatusCode) {
        searchResults = {
          ...searchResults,
          searchString: searchString,
        }

        yield put(actions.saveResults({
          httpResponseMessage,
          searchString,
          searchResults,
          scope,
          newSearch: scope,
          submitting: false,
        }))
      }
      else
        yield put(actions.setShowErrorModal(httpResponseMessage))
    }
    catch (e) {
    }
  }
}

function* watchGetRandom() {
  const totalResults = 5
  const scope = SCOPE.RANDOM
  while (true) {
    const props = yield take('search.random')
    // console.log('watchGetRandom props: ', props)
    const { seed } = props
    props.common.historyPush({ ...props, scope })

    try {
      var items = []

      for (var i = 0; i < totalResults; i++) {
        // Get a single random company
        const randomResults = yield call(api.companiesHouse.getRandomCompany, 1, seed + i)

        const httpResponseMessage = randomResults.httpResponseMessage

        if (httpResponseMessage.isSuccessStatusCode) {

          // add the company to array of companies
          // console.log(`randomResults.items[0]: `, randomResults.items[0])
          items.push(randomResults.items[0])

          const searchResults = {
            ...randomResults,
            items,
            totalResults: items.length,
            searchString: '',
            seed,
          }

          // update the state with the growing list of companies
          yield put(actions.saveResults({
            httpResponseMessage,
            newSearch: false,
            scope,
            searchResults,
            searchString: '',
            seed,
            submitting: (items.length < totalResults ? SCOPE.RANDOM : false),
          }
          ))
        } else
          yield put(actions.setShowErrorModal(httpResponseMessage))
      }
    }
    catch (e) {
    }
  }
}

function* watchUpdateIsFavouriteUsingIds() {
  // Syncs the isFavourite flag of searchResults
  // Used to flag up any item in new searchResults that are a favourite
  while (true) {
    const props = yield take('search.updateIsFavouriteUsingIds')
    // console.log('updateIsFavouriteUsingIds props:',props)

    if (props.searchResults.items.length !== 0) {
      const searchResults = cloneDeep(props.searchResults)

      // Check each item in turn flip its
      searchResults.items.forEach(item =>
        flipIsfavouriteStatusUsingIds({ item, ...props }))

      yield put(actions.updateSearchResults({ searchResults }))
    }
  }
}

const flipIsfavouriteStatusUsingIds = ({ item, ids, isFavourite }) => {
  // Revises isFavourite for the item with matching id
  for (let j = 0; j < item.ids.length; j++)
    if (ids.indexOf(item.ids[j]) > -1) {
      item.isFavourite = isFavourite
      break
    }
}

function* watchSetIsFavouriteById() {
  // Syncs the isFavourite flag of searchResults
  while (true) {
    const props = yield take('search.setIsFavouriteById')
    // console.log('watchSetIsFavouriteById props:', props)

    if (props.searchResults.items.length !== 0) {
      const searchResults = cloneDeep(props.searchResults)
      setIsFavouriteById({ ...props, searchResults })
      yield put(actions.updateSearchResults({ searchResults }))
    }
  }
}

const setIsFavouriteById = props => {
  // console.log('setIsFavouriteById props:', props)
  props.searchResults.items.every(item => {
    if (item._id === props._id) {
      // if (item.ids.indexOf(_id) > -1) { TODO: Check this and replace previous line
      item.isFavourite = props.isFavourite
      return false;
    }
    return true;
  })
}

function* watchSyncWithFavourites() {
  // Syncs the isFavourite flag of searchResults
  while (true) {
    const props = yield take('search.syncWithFavourites')
    // console.log('search.syncWithFavourites props:', props)
    const { favourites } = props
    const searchResults = cloneDeep(props.searchResults)

    if (searchResults.kind === KIND.SEARCH.COMPANIES) {
      searchResults.items.forEach(item => {
        const index = favourites.companies.map(e => e._id).indexOf(item._id)
        item.isFavourite = index === -1 ? false : true
      })
    }
    else searchResults.items.forEach(item => {
      // if not searching companies, results can be either people
      // or companies so we need to analyse both favourite lists
      if (flipIsfavouriteStatus(item, favourites))
        item.isFavourite = !item.isFavourite
    })
    yield put(actions.updateSearchResults({ searchResults }))
  }
}

const flipIsfavouriteStatus = (item, favourites) => {
  // Return true is favourite status need to be flipped
  for (let i = 0; i < favourites.companies.length; i++)
    for (let j = 0; j < item.ids.length; j++)
      if (favourites.companies[i].ids.indexOf(item.ids[j]) > -1) {
        item._id = favourites.companies[i]._id
        return (item.isFavourite === false)
      }

  for (let i = 0; i < favourites.people.length; i++)
    for (let j = 0; j < item.ids.length; j++)
      if (favourites.people[i].ids.indexOf(item.ids[j]) > -1) {
        item._id = favourites.people[i]._id
        return (item.isFavourite === false)
      }

  return (item.isFavourite === true)
}

function* watchSetIsFavouriteByIndex() {
  while (true) {
    const props = yield take('search.setIsFavouriteByIndex')
    // console.log('search.setIsFavouriteByIndex props: ', props)
    const { index, isFavourite } = props
    const searchResults = cloneDeep(props.searchResults)

    if (index !== undefined)
      searchResults.items[index].isFavourite = isFavourite

    yield put(actions.updateSearchResults({ searchResults }))
  }
}

export const actions = {
  searchStringChanged: searchString =>
    ({ type: 'search.searchStringChanged', searchString }),
  submit: props =>
    ({ ...props, type: 'search.submit' }),
  random: props =>
    ({ ...props, type: 'search.random' }),
  saveResults: props =>
    ({ ...props, type: 'search.saveResults' }),
  setSynced: props =>
    ({ ...props, type: 'search.setSynced' }),
  updateSearchResults: props =>
    ({ ...props, type: 'search.updateSearchResults' }),
  syncWithFavourites: props =>
    ({ ...props, type: 'search.syncWithFavourites' }),
  setIsFavouriteByIndex: (searchResults, index, isFavourite) =>
    ({ type: 'search.setIsFavouriteByIndex', searchResults, index, isFavourite }),
  updateIsFavouriteUsingIds: props =>
    ({ ...props, type: 'search.updateIsFavouriteUsingIds' }),
  setIsFavouriteById: props =>
    ({ ...props, type: 'search.setIsFavouriteById' }),
  setShowErrorModal: props =>
    ({ ...props, type: 'search.setShowErrorModal' }),
}

export const selectors = {
}

export function* sagas() {
  yield all([
    watchSubmit(),
    watchGetRandom(),
    watchSyncWithFavourites(),
    watchSetIsFavouriteByIndex(),
    watchUpdateIsFavouriteUsingIds(),
    watchSetIsFavouriteById(),
  ])
}

